import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Environment } from "../../environment";
import { AccountService } from "./account.service";

interface VoiceTranscript {
    text: string;
    isFinal: boolean;
}

export interface PreFetchedTtsAudio {
    buffer: ArrayBuffer;
    mimeType: string;
}

interface ActiveStreamingPlayback {
    audioElement: HTMLAudioElement;
    mediaSource?: MediaSource;
    sourceBuffer?: SourceBuffer;
    abortController?: AbortController;
    streamCompleted: boolean;
    blobUrl?: string;
}

@Injectable({
    providedIn: "root",
})
export class VoiceService {
    private recordingSubject?: Subject<VoiceTranscript>;
    private shouldKeepRecording = false;
    private mediaStream?: MediaStream;
    private audioContext?: AudioContext;
    private sourceNode?: MediaStreamAudioSourceNode;
    private workletNode?: AudioWorkletNode;
    private pcmSamples: Float32Array[] = [];
    private consumedChunkEnd = 0;
    private lastEmittedStrippedText = "";
    private readonly pcmSampleRate = 16000;
    private transcriptAbortController?: AbortController;
    private sendIntervalId?: number;
    private activeReadTextResolver?: () => void;
    private activePlaybackAbortController?: AbortController;
    private activeStreamingPlayback?: ActiveStreamingPlayback;
    private playbackRequestId = 0;
    private prefetchAbortController?: AbortController;

    public constructor(private accountService: AccountService) {
    }

    public startRecording(_language = "en-US"): Observable<VoiceTranscript> {
        if (this.recordingSubject && this.shouldKeepRecording) {
            return this.recordingSubject.asObservable();
        }

        this.shouldKeepRecording = true;
        this.pcmSamples = [];
        this.consumedChunkEnd = 0;
        this.lastEmittedStrippedText = "";
        this.recordingSubject = new Subject<VoiceTranscript>();

        navigator.mediaDevices.getUserMedia({
            audio: {
                noiseSuppression: true,
                echoCancellation: true,
                autoGainControl: true,
            },
        })
            .then(async (stream) => {
                if (!this.shouldKeepRecording) {
                    stream.getTracks().forEach((t) => t.stop());
                    this.completeRecording();
                    return;
                }

                this.mediaStream = stream;

                const audioContext = new AudioContext({ sampleRate: this.pcmSampleRate });
                this.audioContext = audioContext;

                const processorBlob = new Blob([`
                    class PcmCaptureProcessor extends AudioWorkletProcessor {
                        process(inputs) {
                            const channel = inputs[0]?.[0];
                            if (channel) { this.port.postMessage(channel.slice()); }
                            return true;
                        }
                    }
                    registerProcessor("pcm-capture-processor", PcmCaptureProcessor);
                    `], { type: "application/javascript" });
                const processorUrl = URL.createObjectURL(processorBlob);
                try {
                    await audioContext.audioWorklet.addModule(processorUrl);
                } finally {
                    URL.revokeObjectURL(processorUrl);
                }

                if (!this.shouldKeepRecording) {
                    this.cleanupRecordingState();
                    return;
                }

                const sourceNode = audioContext.createMediaStreamSource(stream);
                this.sourceNode = sourceNode;

                const workletNode = new AudioWorkletNode(audioContext, "pcm-capture-processor");
                this.workletNode = workletNode;

                workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
                    this.pcmSamples.push(event.data);
                };

                sourceNode.connect(workletNode);

                this.sendIntervalId = window.setInterval(() => {
                    void this.sendAudioToServer(false);
                }, 2000);
            })
            .catch((error: unknown) => {
                this.emitRecordingError(this.asError(error, "Unable to access microphone."));
            });

        return this.recordingSubject.asObservable();
    }

    public stopRecording(): void {
        this.shouldKeepRecording = false;
        this.clearSendInterval();
        this.disconnectAudioNodes();

        const ctx = this.audioContext;
        this.audioContext = undefined;

        void (async () => {
            if (ctx) {
                try {
                    await ctx.close();
                } catch {
                    // Ignore close errors.
                }
            }
            this.stopMediaStream();
            void this.sendAudioToServer(true);
        })();
    }

    public readText(text: string, onNetworkDone?: () => void): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve();
        }

        const normalizedText = text.trim();
        if (!normalizedText) {
            return Promise.resolve();
        }

        const requestId = ++this.playbackRequestId;

        this.cancelActivePlayback();
        this.resolveActiveReadText();

        return new Promise<void>((resolve, reject) => {
            this.activeReadTextResolver = resolve;
            const abortController = new AbortController();
            this.activePlaybackAbortController = abortController;

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            const authToken = this.accountService.authToken;
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }

            const finish = () => {
                if (requestId !== this.playbackRequestId) {
                    return;
                }

                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }

                resolve();
            };

            const fail = (error: unknown) => {
                console.error("TTS playback failed", error);
                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }
                reject(error);
            };

            void fetch(`${Environment.ApiUri}/ai/tts/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ input: normalizedText }),
                signal: abortController.signal,
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`TTS request failed (${response.status} ${response.statusText}).`);
                }

                await this.playStreamingResponse(response, requestId, abortController.signal, finish, onNetworkDone);
            }).catch((error: unknown) => {
                if (abortController.signal.aborted) {
                    finish();
                    return;
                }

                fail(error);
            });
        });
    }

    public interruptRead(): void {
        this.playbackRequestId++;
        this.cancelActivePlayback();
        this.resolveActiveReadText();
        this.prefetchAbortController?.abort();
        this.prefetchAbortController = undefined;
    }

    private cancelActivePlayback(): void {
        if (this.activePlaybackAbortController && !this.activePlaybackAbortController.signal.aborted) {
            this.activePlaybackAbortController.abort();
        }

        this.clearActivePlayback();
    }

    private resolveActiveReadText(): void {
        if (!this.activeReadTextResolver) {
            return;
        }

        const resolve = this.activeReadTextResolver;
        this.activeReadTextResolver = undefined;
        resolve();
    }

    private clearActivePlayback(): void {
        this.activePlaybackAbortController = undefined;

        const playback = this.activeStreamingPlayback;
        this.activeStreamingPlayback = undefined;

        if (playback) {
            // Abort reader first so the sourceopen handler stops feeding data.
            if (playback.abortController && !playback.abortController.signal.aborted) {
                playback.abortController.abort();
            }

            try { playback.audioElement.pause(); } catch { /* ignore */ }

            // Close MediaSource before revoking its object URL; Firefox requires this order.
            if (playback.mediaSource && playback.mediaSource.readyState === "open") {
                try {
                    playback.mediaSource.endOfStream();
                } catch {
                    // Ignore endOfStream errors during cleanup.
                }
            }

            // Remove the src reference before revoking the blob URL; setting src=""
            // can trigger a spurious load in Firefox, so use removeAttribute instead.
            try {
                playback.audioElement.removeAttribute("src");
                playback.audioElement.load();
            } catch { /* ignore */ }

            if (playback.blobUrl) {
                URL.revokeObjectURL(playback.blobUrl);
            }

            try { playback.audioElement.remove(); } catch { /* ignore */ }
        }
    }

    public async prefetchTtsAudio(text: string): Promise<PreFetchedTtsAudio | null> {
        if (typeof window === "undefined") {
            return null;
        }

        const normalizedText = text.trim();
        if (!normalizedText) {
            return null;
        }

        this.prefetchAbortController?.abort();
        const abortController = new AbortController();
        this.prefetchAbortController = abortController;

        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const authToken = this.accountService.authToken;
        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        }

        try {
            const response = await fetch(`${Environment.ApiUri}/ai/tts/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ input: normalizedText }),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(`TTS prefetch failed (${response.status} ${response.statusText}).`);
            }

            const mimeType = this.detectAudioMimeType(response);
            const buffer = await response.arrayBuffer();

            if (abortController.signal.aborted) {
                return null;
            }

            return { buffer, mimeType };
        } catch (error) {
            if (abortController.signal.aborted) {
                return null;
            }
            console.error("TTS prefetch error", error);
            return null;
        } finally {
            if (this.prefetchAbortController === abortController) {
                this.prefetchAbortController = undefined;
            }
        }
    }

    public playPreFetchedTtsAudio(data: PreFetchedTtsAudio): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve();
        }

        const requestId = ++this.playbackRequestId;
        this.cancelActivePlayback();
        this.resolveActiveReadText();

        return new Promise<void>((resolve) => {
            this.activeReadTextResolver = resolve;

            const abortController = new AbortController();
            this.activePlaybackAbortController = abortController;

            if (requestId !== this.playbackRequestId) {
                resolve();
                return;
            }

            const blob = new Blob([data.buffer], { type: data.mimeType || "audio/webm" });
            const blobUrl = URL.createObjectURL(blob);

            const audioElement = document.createElement("audio");
            audioElement.controls = false;
            audioElement.preload = "auto";
            audioElement.src = blobUrl;

            this.activeStreamingPlayback = {
                audioElement,
                streamCompleted: true,
                blobUrl,
            };

            const finish = () => {
                if (requestId !== this.playbackRequestId) {
                    return;
                }
                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }
                resolve();
            };

            audioElement.addEventListener("ended", finish);
            audioElement.addEventListener("error", () => {
                window.console.warn("Prefetched audio element error:", audioElement.error);
                finish();
            });

            audioElement.play().catch(() => {
                window.console.log("Prefetched audio play blocked.");
            });
        });
    }

    private async playStreamingResponse(
        response: Response,
        requestId: number,
        abortSignal: AbortSignal,
        finish: () => void,
        onNetworkDone?: () => void,
    ): Promise<void> {
        if (!response.body) {
            throw new Error("TTS response did not include a readable stream.");
        }

        const mimeType = this.detectAudioMimeType(response);

        // If the browser's MSE implementation doesn't support the format (e.g. Firefox
        // does not support audio/mpeg in MSE), buffer the whole stream and play as a blob.
        if (!MediaSource.isTypeSupported(mimeType)) {
            await this.playStreamingResponseAsBlob(response, mimeType, requestId, abortSignal, finish, onNetworkDone);
            return;
        }

        const audioElement = document.createElement("audio");
        audioElement.controls = false;
        audioElement.preload = "none";

        const mediaSource = new MediaSource();
        const objectUrl = URL.createObjectURL(mediaSource);
        audioElement.src = objectUrl;

        const abortController = new AbortController();

        this.activeStreamingPlayback = {
            audioElement,
            mediaSource,
            abortController,
            streamCompleted: false,
            blobUrl: objectUrl,
        };

        const playback = this.activeStreamingPlayback;
        const reader = response.body.getReader();

        const waitForSourceBufferReady = (sb: SourceBuffer): Promise<void> => {
            return new Promise<void>((resolve) => {
                if (!sb.updating) {
                    resolve();
                    return;
                }
                sb.onupdateend = () => resolve();
            });
        };

        mediaSource.addEventListener("sourceopen", async () => {
            try {
                let sourceBuffer: SourceBuffer;
                try {
                    sourceBuffer = mediaSource.addSourceBuffer(mimeType);
                } catch {
                    // Fallback: try codec-qualified webm, then bare webm.
                    const fallback = this.pickSupportedMseType("audio/webm; codecs=opus", "audio/webm");
                    sourceBuffer = mediaSource.addSourceBuffer(fallback);
                }
                playback.sourceBuffer = sourceBuffer;
                sourceBuffer.mode = "sequence";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        playback.streamCompleted = true;
                        if (mediaSource.readyState === "open") {
                            if (playback.sourceBuffer?.updating) {
                                await waitForSourceBufferReady(playback.sourceBuffer);
                            }
                            if (mediaSource.readyState === "open") {
                                mediaSource.endOfStream();
                            }
                        }
                        onNetworkDone?.();
                        break;
                    }

                    if (requestId !== this.playbackRequestId || abortSignal.aborted || abortController.signal.aborted) {
                        reader.releaseLock();
                        finish();
                        return;
                    }

                    if (!value || !playback.sourceBuffer) {
                        continue;
                    }

                    try {
                        if (playback.sourceBuffer.updating) {
                            await waitForSourceBufferReady(playback.sourceBuffer);
                        }
                        // Re-check state after await: the MediaSource may have been closed during the wait.
                        if (mediaSource.readyState !== "open" || requestId !== this.playbackRequestId || abortSignal.aborted || abortController.signal.aborted) {
                            reader.releaseLock();
                            finish();
                            return;
                        }
                        playback.sourceBuffer.appendBuffer(value);
                    } catch (error: unknown) {
                        if (error instanceof DOMException && error.name === "InvalidStateError") {
                            // SourceBuffer is no longer usable (MediaSource was closed/ended during interruption).
                            break;
                        }
                        const message = error instanceof Error ? error.message : String(error);
                        if (message.includes("SourceBuffer") && message.includes("updating")) {
                            await waitForSourceBufferReady(playback.sourceBuffer);
                            try {
                                playback.sourceBuffer!.appendBuffer(value);
                            } catch {
                                // Ignore second append failure.
                            }
                        } else {
                            console.warn("SourceBuffer append error:", error);
                        }
                    }
                }
            } catch (error: unknown) {
                console.error("MSE playback error:", error);
                if (mediaSource.readyState === "open") {
                    if (playback.sourceBuffer?.updating) {
                        try { await waitForSourceBufferReady(playback.sourceBuffer); } catch { /* ignore */ }
                    }
                    if (mediaSource.readyState === "open") {
                        try { mediaSource.endOfStream(); } catch { /* ignore */ }
                    }
                }
                finish();
                return;
            } finally {
                reader.releaseLock();
            }
        });

        audioElement.addEventListener("ended", () => {
            if (requestId === this.playbackRequestId && !abortSignal.aborted) {
                finish();
            }
        });

        audioElement.addEventListener("error", () => {
            // Log audio errors but don't fail the promise.
            window.console.warn("Audio element error:", audioElement.error);
        });

        try {
            await audioElement.play();
        } catch {
            // Autoplay may be blocked; the audio will play once the user interacts.
            window.console.log("Audio play blocked, waiting for user interaction.");
        }
    }

    private async playStreamingResponseAsBlob(
        response: Response,
        mimeType: string,
        requestId: number,
        abortSignal: AbortSignal,
        finish: () => void,
        onNetworkDone?: () => void,
    ): Promise<void> {
        const reader = response.body!.getReader();
        const chunks: Uint8Array[] = [];

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                if (requestId !== this.playbackRequestId || abortSignal.aborted) {
                    finish();
                    return;
                }
                if (value) {
                    chunks.push(value);
                }
            }
        } finally {
            reader.releaseLock();
        }

        onNetworkDone?.();

        if (requestId !== this.playbackRequestId || abortSignal.aborted) {
            finish();
            return;
        }

        const blob = new Blob(chunks as BlobPart[], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);

        const audioElement = document.createElement("audio");
        audioElement.controls = false;
        audioElement.preload = "auto";
        audioElement.src = blobUrl;

        this.activeStreamingPlayback = {
            audioElement,
            streamCompleted: true,
            blobUrl,
        };

        audioElement.addEventListener("ended", () => {
            if (requestId === this.playbackRequestId && !abortSignal.aborted) {
                finish();
            }
        });

        audioElement.addEventListener("error", () => {
            window.console.warn("Audio element error:", audioElement.error);
            finish();
        });

        try {
            await audioElement.play();
        } catch {
            // Autoplay may be blocked; the audio will play once the user interacts.
            window.console.log("Audio play blocked, waiting for user interaction.");
        }
    }

    private detectAudioMimeType(response: Response): string {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("audio/ogg")) {
            return this.pickSupportedMseType("audio/ogg; codecs=opus", "audio/ogg");
        }
        if (contentType.includes("audio/webm")) {
            return this.pickSupportedMseType("audio/webm; codecs=opus", "audio/webm");
        }
        if (contentType.includes("audio/mpeg") || contentType.includes("audio/mp3")) {
            return "audio/mpeg";
        }
        if (contentType.includes("audio/mp4") || contentType.includes("audio/aac")) {
            return this.pickSupportedMseType("audio/mp4; codecs=mp4a.40.2", "audio/mp4");
        }
        return this.pickSupportedMseType("audio/webm; codecs=opus", "audio/ogg; codecs=opus", "audio/webm");
    }

    private pickSupportedMseType(...candidates: string[]): string {
        for (const type of candidates) {
            if (MediaSource.isTypeSupported(type)) {
                return type;
            }
        }
        return candidates[candidates.length - 1];
    }

    private completeRecording(): void {
        this.recordingSubject?.complete();
        this.cleanupRecordingState();
    }

    private emitRecordingError(error: Error): void {
        this.recordingSubject?.error(error);
        this.cleanupRecordingState();
    }

    private cleanupRecordingState(): void {
        this.shouldKeepRecording = false;
        this.pcmSamples = [];
        this.consumedChunkEnd = 0;
        this.lastEmittedStrippedText = "";
        this.recordingSubject = undefined;

        this.clearSendInterval();
        this.disconnectAudioNodes();

        const ctx = this.audioContext;
        this.audioContext = undefined;
        ctx?.close().catch(() => { /* Ignore close errors. */ });

        this.stopMediaStream();

        if (this.transcriptAbortController && !this.transcriptAbortController.signal.aborted) {
            this.transcriptAbortController.abort();
        }
        this.transcriptAbortController = undefined;
    }

    private async sendAudioToServer(isFinal: boolean): Promise<void> {
        if (!this.recordingSubject) {
            return;
        }

        if (isFinal) {
            // Use the full accumulated audio so Whisper has complete context.
            if (this.pcmSamples.length === 0) {
                this.completeRecording();
                return;
            }
            // pcmSamples is cleared by cleanupRecordingState() via completeRecording().
        } else {
            if (this.pcmSamples.length === 0) {
                return;
            }
            // Skip silent/minimal chunks to avoid [NO AUDIO] server responses.
            const totalSamples = this.pcmSamples.reduce((sum, chunk) => sum + chunk.length, 0);
            if (totalSamples < 3200) {
                return;
            }
        }

        // Cancel any in-flight partial request before starting a new one.
        if (this.transcriptAbortController && !this.transcriptAbortController.signal.aborted) {
            this.transcriptAbortController.abort();
        }

        const consumedBefore = this.consumedChunkEnd;
        this.consumedChunkEnd = this.pcmSamples.length;
        const audioBlob = this.encodeWav(this.mergePcmSamples(this.pcmSamples), this.pcmSampleRate);
        const abortController = new AbortController();
        this.transcriptAbortController = abortController;

        const headers: Record<string, string> = {
            "Content-Type": "audio/wav",
        };
        const authToken = this.accountService.authToken;
        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        }

        try {
            const response = await fetch(`${Environment.ApiUri}/ai/process-speech`, {
                method: "POST",
                headers,
                body: audioBlob,
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Speech processing failed (${response.status} ${response.statusText}).`);
            }

            if (!response.body) {
                throw new Error("Speech processing response did not include a readable stream.");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let finalTranscript = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() ?? "";

                for (const line of lines) {
                    if (!line.startsWith("data: ")) {
                        continue;
                    }

                    const data = line.slice(6).trim();
                    if (!data || data === "[DONE]") {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(data) as { text?: string };
                        const text = parsed?.text?.trim();
                        if (text) {
                            window.console.log("Received transcript chunk before stripping:", text);
                            const stripped = text.replace(/\[[^\]]*\]/g, "").replace(/\(.*?\)/g, "").replace(/\.$/, "").trim();
                            if (stripped) {
                                window.console.log("Received transcript chunk:", stripped);
                                if (isFinal) {
                                    // Accumulate all chunks into one emission so the component
                                    // replaces the partial transcript with a single accurate value.
                                    finalTranscript += (finalTranscript ? " " : "") + stripped;
                                } else if (stripped !== this.lastEmittedStrippedText) {
                                    this.lastEmittedStrippedText = stripped;
                                    this.recordingSubject?.next({ text: stripped, isFinal: false });
                                } else {
                                    this.discardRedundantPcmSamples(consumedBefore);
                                }
                            } else if (!isFinal) {
                                this.discardLeadingProcessedSamples();
                            }
                        } else if (!isFinal) {
                            this.discardLeadingProcessedSamples();
                        }
                    } catch {
                        // Ignore malformed SSE data.
                    }
                }
            }

            if (isFinal) {
                if (finalTranscript) {
                    this.recordingSubject?.next({ text: finalTranscript, isFinal: true });
                    this.lastEmittedStrippedText = finalTranscript;
                }
                this.completeRecording();
            }
        } catch (error: unknown) {
            if (abortController.signal.aborted) {
                this.consumedChunkEnd = consumedBefore;
                if (isFinal) {
                    this.completeRecording();
                }
                return;
            }
            if (isFinal) {
                this.emitRecordingError(this.asError(error, "Speech processing request failed."));
            } else {
                console.error("Partial transcript request failed:", error);
            }
        } finally {
            if (this.transcriptAbortController === abortController) {
                this.transcriptAbortController = undefined;
            }
        }
    }

    private clearSendInterval(): void {
        if (this.sendIntervalId !== undefined) {
            window.clearInterval(this.sendIntervalId);
            this.sendIntervalId = undefined;
        }
    }

    private stopMediaStream(): void {
        const stream = this.mediaStream;
        this.mediaStream = undefined;
        stream?.getTracks().forEach((t) => t.stop());
    }

    private encodeWav(samples: Float32Array, sampleRate: number): Blob {
        const numChannels = 1;
        const bitsPerSample = 16;
        const dataSize = samples.length * 2;
        const buffer = new ArrayBuffer(44 + dataSize);
        const view = new DataView(buffer);

        const write = (offset: number, str: string) => {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(offset + i, str.charCodeAt(i));
            }
        };

        write(0, "RIFF");
        view.setUint32(4, 36 + dataSize, true);
        write(8, "WAVE");
        write(12, "fmt ");
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
        view.setUint16(32, numChannels * (bitsPerSample / 8), true);
        view.setUint16(34, bitsPerSample, true);
        write(36, "data");
        view.setUint32(40, dataSize, true);

        let offset = 44;
        for (let i = 0; i < samples.length; i++) {
            const s = Math.max(-1, Math.min(1, samples[i]));
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
            offset += 2;
        }

        return new Blob([buffer], { type: "audio/wav" });
    }

    private mergePcmSamples(chunks: Float32Array[]): Float32Array {
        const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const merged = new Float32Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            merged.set(chunk, offset);
            offset += chunk.length;
        }
        return merged;
    }

    private disconnectAudioNodes(): void {
        try {
            this.sourceNode?.disconnect();
        } catch {
            // Ignore disconnect errors.
        }
        this.sourceNode = undefined;

        try {
            this.workletNode?.port.close();
            this.workletNode?.disconnect();
        } catch {
            // Ignore disconnect errors.
        }
        this.workletNode = undefined;
    }

    private asError(error: unknown, fallbackMessage: string): Error {
        if (error instanceof Error) {
            return error;
        }

        return new Error(fallbackMessage);
    }

    private discardRedundantPcmSamples(consumedBefore: number): void {
        this.pcmSamples = [
            ...this.pcmSamples.slice(0, consumedBefore),
            ...this.pcmSamples.slice(this.consumedChunkEnd),
        ];
        this.consumedChunkEnd = consumedBefore;
    }

    private discardLeadingProcessedSamples(): void {
        // Drop processed chunks (0..consumedChunkEnd-1), keep the rest
        this.pcmSamples = this.pcmSamples.slice(this.consumedChunkEnd);
        this.consumedChunkEnd = 0;
        this.lastEmittedStrippedText = "";
    }

}
